import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { statisticsService, seedService, companyService, reminderDashboardService } from "../services/api";
import { Building2, Users, UserCheck, Briefcase, Database, ArrowRight, TrendingUp, Bell, AlertTriangle, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function DashboardPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentCompanies, setRecentCompanies] = useState([]);
  const [reminderData, setReminderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsResponse, companiesResponse, remindersResponse] = await Promise.all([
          statisticsService.get(),
          companyService.getAll({ limit: 5 }),
          reminderDashboardService.get().catch(() => ({ data: null }))
        ]);
        setStats(statsResponse.data);
        setRecentCompanies(companiesResponse.data.items || []);
        setReminderData(remindersResponse.data);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSeedData = async () => {
    if (!window.confirm("This will replace all existing demo data. Continue?")) {
      return;
    }
    setSeeding(true);
    try {
      await seedService.seedData();
      toast.success("Demo data seeded successfully!");
      // Refresh stats
      const statsResponse = await statisticsService.get();
      setStats(statsResponse.data);
      const companiesResponse = await companyService.getAll({ limit: 5 });
      setRecentCompanies(companiesResponse.data.items || []);
    } catch (error) {
      toast.error("Failed to seed data: " + (error.response?.data?.detail || error.message));
    } finally {
      setSeeding(false);
    }
  };

  const statCards = [
    { label: "Total Companies", value: stats?.total_companies || 0, icon: Building2, color: "bg-blue-50 text-blue-700" },
    { label: "Active Companies", value: stats?.active_companies || 0, icon: TrendingUp, color: "bg-green-50 text-green-700" },
    { label: "Total Entities", value: stats?.total_entities || 0, icon: Users, color: "bg-purple-50 text-purple-700" },
    { label: "Directors", value: stats?.total_directors || 0, icon: UserCheck, color: "bg-orange-50 text-orange-700" },
    { label: "Shareholders", value: stats?.total_shareholders || 0, icon: Briefcase, color: "bg-cyan-50 text-cyan-700" }
  ];

  if (loading) {
    return (
      <div className="p-6 sm:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Welcome back, {user?.name}</p>
        </div>
        {isAdmin && (
          <Button
            onClick={handleSeedData}
            disabled={seeding}
            className="btn-secondary flex items-center gap-2"
            data-testid="seed-data-btn"
          >
            <Database className="w-4 h-4" />
            {seeding ? "Seeding..." : "Seed Demo Data"}
          </Button>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {statCards.map((stat, index) => (
          <div key={index} className="enterprise-card p-4" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${stat.color}`}>
                <stat.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">{stat.label}</p>
                <p className="text-xl font-semibold text-slate-900">{stat.value.toLocaleString()}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Reminder Widgets */}
      {reminderData && (reminderData.overdue_count > 0 || reminderData.due_soon_count > 0 || reminderData.upcoming_count > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="reminder-widgets">
          {/* Overdue */}
          <div className="enterprise-card border-l-4 border-l-red-500" data-testid="widget-overdue">
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <h3 className="text-sm font-semibold text-red-700 uppercase tracking-wider">Overdue ({reminderData.overdue_count})</h3>
              </div>
              {reminderData.overdue.length > 0 ? (
                <div className="space-y-2">
                  {reminderData.overdue.slice(0, 5).map((r) => (
                    <div key={r.id} className="flex items-center justify-between text-sm" data-testid={`overdue-item-${r.id}`}>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">{r.title}</p>
                        <p className="text-xs text-slate-500">{r.company_name}{r.company_code ? ` (${r.company_code})` : ''}</p>
                      </div>
                      <span className="text-xs text-red-600 font-medium whitespace-nowrap ml-2">{r.next_due_date}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400">No overdue items</p>
              )}
            </div>
          </div>

          {/* Due Soon */}
          <div className="enterprise-card border-l-4 border-l-amber-500" data-testid="widget-due-soon">
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-5 h-5 text-amber-500" />
                <h3 className="text-sm font-semibold text-amber-700 uppercase tracking-wider">Due Soon ({reminderData.due_soon_count})</h3>
              </div>
              {reminderData.due_soon.length > 0 ? (
                <div className="space-y-2">
                  {reminderData.due_soon.slice(0, 5).map((r) => (
                    <div key={r.id} className="flex items-center justify-between text-sm" data-testid={`due-soon-item-${r.id}`}>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">{r.title}</p>
                        <p className="text-xs text-slate-500">{r.company_name}{r.company_code ? ` (${r.company_code})` : ''}</p>
                      </div>
                      <span className="text-xs text-amber-600 font-medium whitespace-nowrap ml-2">{r.next_due_date}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400">No items due soon</p>
              )}
            </div>
          </div>

          {/* Upcoming */}
          <div className="enterprise-card border-l-4 border-l-blue-500" data-testid="widget-upcoming">
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Bell className="w-5 h-5 text-blue-500" />
                <h3 className="text-sm font-semibold text-blue-700 uppercase tracking-wider">Upcoming ({reminderData.upcoming_count})</h3>
              </div>
              {reminderData.upcoming.length > 0 ? (
                <div className="space-y-2">
                  {reminderData.upcoming.slice(0, 5).map((r) => (
                    <div key={r.id} className="flex items-center justify-between text-sm" data-testid={`upcoming-item-${r.id}`}>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">{r.title}</p>
                        <p className="text-xs text-slate-500">{r.company_name}{r.company_code ? ` (${r.company_code})` : ''}</p>
                      </div>
                      <span className="text-xs text-blue-600 font-medium whitespace-nowrap ml-2">{r.next_due_date}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400">No upcoming items</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Recent Companies */}
      <div className="enterprise-card">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-medium text-slate-900">Recent Companies</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/companies")}
            className="text-blue-900 hover:text-blue-700"
            data-testid="view-all-companies-btn"
          >
            View all
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        
        {recentCompanies.length === 0 ? (
          <div className="p-8 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No companies found</p>
            <p className="text-sm text-slate-400 mt-1">
              {isAdmin ? "Click 'Seed Demo Data' to add sample companies" : "Companies will appear here once added"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Company Name</th>
                  <th>Industry</th>
                  <th>Status</th>
                  <th>Incorporation</th>
                </tr>
              </thead>
              <tbody>
                {recentCompanies.map((company) => (
                  <tr 
                    key={company.id} 
                    className="cursor-pointer"
                    onClick={() => navigate(`/companies/${company.id}`)}
                    data-testid={`company-row-${company.company_code}`}
                  >
                    <td className="font-medium text-slate-900">{company.company_code}</td>
                    <td>{company.name}</td>
                    <td>{company.industry || '-'}</td>
                    <td>
                      <span className={`badge ${
                        company.status === 'active' ? 'badge-active' :
                        company.status === 'dormant' ? 'badge-dormant' : 'badge-dissolved'
                      }`}>
                        {company.status}
                      </span>
                    </td>
                    <td>{company.incorporation_date || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
